import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Msg, panelGet } from './kit';
import { Badge } from './email/emailKit';
import { type AdminTarget, adminHref, openAdmin } from './adminNav';
import { ADMIN_TABS, tabLink } from './adminTabs';
import { tabKey, useAdminAccess } from './adminAccess';

/**
 * Global admin search (owner request 2026-10-05): Cmd/Ctrl+K anywhere in the
 * admin, or the sidebar's Search button. One box for "take me to that page"
 * (groups and their sub-tabs, matched here in the browser) and "find that
 * record" (users, addresses, techs, campaigns, messages… from GET
 * lookup/search). Every result is an adminNav deep link, so Enter is an in-app
 * navigation and Cmd/Ctrl+Enter opens the same URL in a new tab.
 */

const OPEN_EVENT = 'utech:admin-palette';
/** For the sidebar button (and anything else that wants to open the palette). */
export const openCommandPalette = () => document.dispatchEvent(new Event(OPEN_EVENT));

const IS_MAC = typeof navigator !== 'undefined'
  && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
export const PALETTE_SHORTCUT = IS_MAC ? '⌘K' : 'Ctrl K';
const MOD = IS_MAC ? '⌘' : 'Ctrl';

interface ViewLite { key: string; title: string; description?: string }

interface AltLink { label: string; link: AdminTarget }
/** One row, whatever produced it: a group, a sub-tab, a recent pick or a server hit. */
interface Entry {
  key: string; label: string; sub?: string; badge?: string; link: AdminTarget; alt?: AltLink[];
}
interface Sect { key: string; title: string; entries: Entry[]; action?: { label: string; onClick: () => void } }

type SearchResp = {
  q?: string; kind?: string; took_ms?: number;
  groups: { key: string; title: string; items: any[] }[];
};

// ---------------------------------------------------------------------------
// Recent picks — this browser only, a convenience, never a record.
// ---------------------------------------------------------------------------
const RECENT_KEY = 'utech_admin_palette_recent';
const RECENT_MAX = 8;
type Recent = { label: string; sub?: string; badge?: string; link: AdminTarget };

/** Server links are data: keep a group and plain scalar params, nothing else. */
const cleanLink = (l: any): AdminTarget | null => {
  if (!l || typeof l !== 'object' || typeof l.group !== 'string' || !l.group.trim()) return null;
  const out: AdminTarget = { group: l.group.trim() };
  Object.entries(l).forEach(([k, v]) => {
    if (k === 'group' || k === 'nonce' || v === null || v === undefined || v === '') return;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') out[k] = String(v);
  });
  return out;
};

const loadRecent = (): Recent[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    if (!Array.isArray(raw)) return [];
    const out: Recent[] = [];
    raw.forEach((r: any) => {
      const link = cleanLink(r?.link);
      if (!link || typeof r.label !== 'string') return;
      out.push({ label: r.label, sub: typeof r.sub === 'string' ? r.sub : undefined,
        badge: typeof r.badge === 'string' ? r.badge : undefined, link });
    });
    return out.slice(0, RECENT_MAX);
  } catch { return []; }
};
const saveRecent = (r: Recent[]) => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(r.slice(0, RECENT_MAX))); } catch { /* private mode */ }
};

// ---------------------------------------------------------------------------
// Matching the admin's own pages
// ---------------------------------------------------------------------------
const norm = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');
// "bounces" finds the page tagged "bounce": a plural is matched by its singular.
const tokens = (q: string) => norm(q).split(/\s+/).filter(Boolean)
  .map(t => (t.length > 4 && /[^s]s$/.test(t) ? t.slice(0, -1) : t));
const atWordStart = (h: string, t: string) => {
  for (let i = h.indexOf(t); i !== -1; i = h.indexOf(t, i + 1)) {
    if (i === 0 || !/[a-z0-9]/.test(h[i - 1])) return true;
  }
  return false;
};
const inOrder = (h: string, t: string) => {
  let j = 0;
  for (let i = 0; i < h.length && j < t.length; i++) if (h[i] === t[j]) j++;
  return j === t.length;
};

/** 0 = no match. Every query word must hit the label or the keywords; a hit
 *  at a word start beats one mid-word, the label beats the keywords, and a
 *  loose in-order letter match on the label is the last resort. `name` (the
 *  tab's own name) breaks ties, so "trl" ranks Content → TRL above the other
 *  Content & TRL tabs. */
const scorePage = (q: string, label: string, extra: string, name = label): number => {
  const L = norm(label);
  const X = norm(extra);
  const N = norm(name);
  const toks = tokens(q);
  if (!toks.length) return 0;
  let s = 0;
  for (const t of toks) {
    if (atWordStart(L, t)) s += 4;
    else if (L.includes(t)) s += 2.5;
    else if (atWordStart(X, t)) s += 2;
    else if (X.includes(t)) s += 1;
    else if (t.length >= 3 && inOrder(L, t)) s += 0.5;
    else return 0;
    if (atWordStart(N, t)) s += 0.5;
  }
  const whole = norm(q).trim();
  if (L === whole || N === whole) s += 6;
  else if (L.startsWith(whole) || N.startsWith(whole)) s += 3;
  return s;
};

const KIND_LABEL: Record<string, string> = {
  email: 'an e-mail address', uuid: 'an id', tech_id: 'a technology id', university_code: 'a university code',
  conversation: 'a conversation id', razorpay: 'a Razorpay id', campaign_id: 'a campaign id',
  ip_intel_ref: 'an IP Intel order ref', hex32: 'a 32-character id', sg_message: 'a SendGrid message id',
  path: 'a page path', digits: 'a number',
};

const badgeText = (b: any): string | undefined => {
  if (typeof b === 'string' || typeof b === 'number') return String(b) || undefined;
  if (b && typeof b === 'object' && (b.label || b.text)) return String(b.label || b.text);
  return undefined;
};
const badgeTone = (b: string): 'blue' | 'amber' | 'red' | 'green' | 'grey' => {
  const s = b.toLowerCase();
  if (/bounc|block|suppress|fail|drop|deleted|refund|spam|denied|error/.test(s)) return 'red';
  if (/pending|trial|defer|hold|lapsed|waiting|open/.test(s)) return 'amber';
  if (/paid|active|deliver|approved|live|resolved/.test(s)) return 'green';
  if (/hidden|archived|old/.test(s)) return 'grey';
  return 'blue';
};

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="rounded bg-grayscale-200 px-1.5 py-0.5 text-[10px] font-sans text-grayscale-1100">
    {children}
  </kbd>
);

const NONE: string[] = [];
const LIST_ID = 'admin-cmdk-list';
const optId = (i: number) => `admin-cmdk-opt-${i}`;

const CommandPalette: React.FC<{
  /** The groups this admin may open (GET admin/views, plus Access control for
   *  a super admin), the ones they hid themselves included. */
  views: ViewLite[];
  /** Groups this admin hid from the sidebar — still reachable here, marked. */
  hidden?: string[];
}> = ({ views, hidden = NONE }) => {
  const access = useAdminAccess();
  // Groups and sub-tabs this admin cannot open never show: not as a page, not
  // as a record's link, not from the recent list (picked before a change).
  const knownGroups = useMemo(() => new Set(views.map(v => v.key)), [views]);
  const linkOk = useCallback((l: AdminTarget) => {
    if (access.legacy || access.superAdmin) return true;
    if (knownGroups.size && !knownGroups.has(l.group)) return false;
    return !l.tab || access.canTab(l.group, tabKey(String(l.tab)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knownGroups, access.legacy, access.superAdmin, access.tabs]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [activeKey, setActiveKey] = useState('');
  const [alt, setAlt] = useState(-1);
  const [recent, setRecent] = useState<Recent[]>(loadRecent);
  const [srv, setSrv] = useState<{ q: string; data: SearchResp | null; error: string; loading: boolean }>(
    { q: '', data: null, error: '', loading: false });
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const prevFocus = useRef<HTMLElement | null>(null);
  const openRef = useRef(false);
  openRef.current = open;
  const seq = useRef(0);

  const show = useCallback(() => {
    if (openRef.current) { inputRef.current?.select(); return; }
    prevFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setRecent(loadRecent());
    setOpen(true);
  }, []);

  /** restore=false when we are navigating away — focus belongs to the new page. */
  const close = useCallback((restore = true) => {
    setOpen(false);
    setQ('');
    setActiveKey('');
    setAlt(-1);
    seq.current++;
    setSrv({ q: '', data: null, error: '', loading: false });
    const el = prevFocus.current;
    prevFocus.current = null;
    if (restore && el && document.contains(el)) {
      try { el.focus({ preventScroll: true }); } catch { /* element gone */ }
    }
  }, []);

  // Cmd/Ctrl+K from anywhere (inputs included), Escape while open. Capture
  // phase on window, so a panel's own document-level Escape (Horizon closes
  // its drawer) does not fire underneath the palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        if (openRef.current) close(true);
        else show();
        return;
      }
      if (openRef.current && e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close(true);
      }
    };
    window.addEventListener('keydown', onKey, true);
    document.addEventListener(OPEN_EVENT, show);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener(OPEN_EVENT, show);
    };
  }, [show, close]);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  // Records: debounced, at least 2 characters, newest request wins.
  useEffect(() => {
    if (!open) return;
    const qq = q.trim();
    const mine = ++seq.current;
    if (qq.length < 2) { setSrv({ q: qq, data: null, error: '', loading: false }); return; }
    setSrv(s => ({ ...s, loading: true, error: '' }));
    const t = window.setTimeout(async () => {
      const { data, error } = await panelGet('lookup', 'search', { q: qq, limit: 6 });
      if (mine !== seq.current) return;
      const ok = data && Array.isArray(data.groups);
      setSrv({ q: qq, data: ok ? data : null, loading: false,
        error: ok ? '' : (error || 'Unexpected answer from lookup/search') });
    }, 200);
    return () => window.clearTimeout(t);
  }, [q, open]);

  const titleOf = useMemo(() => {
    const m: Record<string, string> = {};
    views.forEach(v => { m[v.key] = v.title; });
    return m;
  }, [views]);

  // Every page the palette can jump to: each group, then each sub-tab.
  const pages = useMemo(() => {
    const known = new Set(views.map(v => v.key));
    const out: { entry: Entry; extra: string; name: string }[] = views.map(v => ({
      entry: { key: `page|${v.key}`, label: v.title, sub: v.description || undefined,
        badge: hidden.includes(v.key) ? 'hidden' : undefined, link: { group: v.key } },
      extra: `${v.key} ${v.description || ''}`,
      name: v.title,
    }));
    ADMIN_TABS.forEach(t => {
      if (known.size && !known.has(t.group)) return;
      if (t.group === 'access' && access.legacy) return;
      if ((t.tab && !access.canTab(t.group, tabKey(t.tab))) || !access.canTab(t.group, tabKey(t.name))) return;
      const title = titleOf[t.group];
      out.push({
        entry: {
          key: `tab|${t.group}|${t.name}`,
          label: title ? `${title} → ${t.name}` : t.label,
          sub: t.tab ? undefined : `Opens ${title || t.group} — pick “${t.name}” there`,
          badge: hidden.includes(t.group) ? 'hidden' : undefined,
          link: tabLink(t),
        },
        extra: `${t.group} ${t.tab || ''} ${t.keywords.join(' ')}`,
        name: t.name,
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views, hidden, titleOf, access.legacy, access.superAdmin, access.tabs]);

  const forgetRecent = useCallback(() => { setRecent([]); saveRecent([]); }, []);

  const sections: Sect[] = useMemo(() => {
    const qq = q.trim();
    if (!qq) {
      const out: Sect[] = [];
      if (recent.length) {
        out.push({
          key: 'recent', title: 'Recent', action: { label: 'Clear', onClick: forgetRecent },
          entries: recent.filter(r => linkOk(r.link)).map(r => ({ key: `recent|${adminHref(r.link)}`, label: r.label,
            sub: r.sub, badge: r.badge, link: r.link })),
        });
      }
      out.push({ key: 'goto', title: 'Go to', entries: pages.filter(p => p.entry.key.startsWith('page|')).map(p => p.entry) });
      return out.filter(s => s.entries.length);
    }

    const ranked = pages
      .map(p => ({ e: p.entry, s: scorePage(qq, p.entry.label, p.extra, p.name) }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s || a.e.label.length - b.e.label.length);
    // Loose letter matches only fill in when nothing better matched — no page
    // by a real word and no record from the server ("ravi" is a person, not
    // "Horizon data → Overview").
    const hasRecords = (srv.data?.groups || []).some((g: any) => (g?.items || []).length);
    const scored = ranked.filter(x => x.s >= 1 || (!hasRecords && ranked[0].s < 1));
    // Short queries and page-name queries put pages first; anything else
    // (an address, an id, a name) is more likely a record.
    const strong = scored.length > 0 && scored[0].s >= 4 * tokens(qq).length;
    const pagesFirst = qq.length <= 3 || strong;
    const pageSect: Sect = {
      key: 'nav', title: 'Admin pages', entries: scored.slice(0, pagesFirst ? 8 : 4).map(x => x.e),
    };

    const records: Sect[] = (srv.data?.groups || []).map((g, gi) => {
      const gkey = String(g?.key || gi);
      const entries: Entry[] = [];
      (Array.isArray(g?.items) ? g.items : []).forEach((it: any, i: number) => {
        const link = cleanLink(it?.link);
        if (!link || !linkOk(link)) return;
        const alts: AltLink[] = [];
        (Array.isArray(it.alt) ? it.alt : []).forEach((a: any) => {
          const l = cleanLink(a?.link);
          if (l && a?.label && linkOk(l)) alts.push({ label: String(a.label), link: l });
        });
        entries.push({
          key: `srv|${gkey}|${it.id ?? i}|${adminHref(link)}`,
          label: String(it.label ?? it.id ?? adminHref(link)),
          sub: it.sub ? String(it.sub) : undefined,
          badge: badgeText(it.badge),
          link,
          alt: alts.length ? alts : undefined,
        });
      });
      return { key: `srv|${gkey}`, title: String(g?.title || gkey), entries };
    }).filter(s => s.entries.length);

    const out = pagesFirst ? [pageSect, ...records] : [...records, pageSect];
    return out.filter(s => s.entries.length);
  }, [q, recent, pages, srv.data, forgetRecent, linkOk]);

  // Flat list for the keyboard; keys made unique so the active row is stable
  // while results stream in under it.
  const { rows, flat } = useMemo(() => {
    const seen = new Set<string>();
    const all: Entry[] = [];
    const out = sections.map(s => ({
      ...s,
      entries: s.entries.map(e => {
        let k = e.key;
        for (let n = 2; seen.has(k); n++) k = `${e.key}#${n}`;
        seen.add(k);
        const x = k === e.key ? e : { ...e, key: k };
        all.push(x);
        return x;
      }),
    }));
    return { rows: out, flat: all };
  }, [sections]);

  const found = flat.findIndex(e => e.key === activeKey);
  const activeIdx = flat.length ? (found >= 0 ? found : 0) : -1;
  const activeEntry = activeIdx >= 0 ? flat[activeIdx] : undefined;

  useEffect(() => {
    if (activeIdx < 0) return;
    const el = document.getElementById(optId(activeIdx));
    if (el && listRef.current?.contains(el)) el.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const move = (to: number) => {
    if (!flat.length) return;
    const i = (to + flat.length) % flat.length;
    setActiveKey(flat[i].key);
    setAlt(-1);
  };

  const remember = (r: Recent) => setRecent(prev => {
    const href = adminHref(r.link);
    const next = [r, ...prev.filter(x => adminHref(x.link) !== href)].slice(0, RECENT_MAX);
    saveRecent(next);
    return next;
  });

  const go = (e: Entry, altIdx: number, newTab: boolean) => {
    const a = altIdx >= 0 ? e.alt?.[altIdx] : undefined;
    const link = a ? a.link : e.link;
    remember({ label: e.label, sub: a ? a.label : e.sub, badge: e.badge, link });
    if (newTab) {
      window.open(adminHref(link), '_blank', 'noopener');
      close(true);
    } else {
      close(false);
      openAdmin(link);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); move(activeIdx + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(activeIdx - 1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeEntry) go(activeEntry, alt, e.metaKey || e.ctrlKey);
    } else if (e.key === 'Tab') {
      // Focus stays in the box; Tab walks the active row's secondary links.
      e.preventDefault();
      const n = activeEntry?.alt?.length || 0;
      if (!n) return;
      setAlt(a => (e.shiftKey ? (a <= -1 ? n - 1 : a - 1) : (a >= n - 1 ? -1 : a + 1)));
    }
  };

  if (!open) return null;

  const qq = q.trim();
  const anyAlt = flat.some(e => e.alt?.length);
  const kind = srv.data?.kind && srv.q === qq ? KIND_LABEL[srv.data.kind] : '';
  const status = srv.loading ? 'Searching…'
    : qq ? `${flat.length} result${flat.length === 1 ? '' : 's'}` : '';
  let idx = -1;

  return (
    <div className="fixed inset-0 z-[110] bg-black/40 flex items-start justify-center px-4 pt-[10vh]"
      onMouseDown={e => { if (e.target === e.currentTarget) close(true); }}>
      <div role="dialog" aria-modal="true" aria-label="Search the admin"
        onKeyDown={e => e.stopPropagation()}
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl flex flex-col max-h-[75vh] overflow-hidden border border-grayscale-200">
        <div className="flex items-center gap-2 px-4 border-b border-grayscale-200">
          <svg aria-hidden viewBox="0 0 20 20" className="w-4 h-4 shrink-0 text-grayscale-1100" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="8.5" cy="8.5" r="5.5" /><path d="M13 13l4 4" />
          </svg>
          <input ref={inputRef} autoFocus value={q}
            onChange={e => { setQ(e.target.value); setActiveKey(''); setAlt(-1); }}
            onKeyDown={onKeyDown}
            placeholder="Search pages, people, addresses, techs, campaigns…"
            role="combobox" aria-expanded={flat.length > 0} aria-controls={LIST_ID}
            aria-autocomplete="list" aria-label="Search pages and records"
            aria-activedescendant={activeIdx >= 0 ? optId(activeIdx) : undefined}
            spellCheck={false} autoComplete="off"
            className="flex-1 min-w-0 h-12 bg-transparent text-base text-grayscale-1300 focus:outline-none" />
          {srv.loading && <span className="text-xs text-grayscale-1100 whitespace-nowrap">Searching…</span>}
          <Kbd>Esc</Kbd>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto overscroll-contain py-1">
          {srv.error && srv.q === qq && (
            <div className="px-3 pt-2">
              <Msg kind="error">Record search failed: {srv.error}. Admin pages still work.</Msg>
            </div>
          )}
          <div role="listbox" id={LIST_ID} aria-label="Results">
            {rows.map(s => (
              <div key={s.key} role="group" aria-labelledby={`${LIST_ID}-${s.key}`} className="pb-1">
                <div className="flex items-center justify-between px-4 pt-2 pb-1">
                  <div id={`${LIST_ID}-${s.key}`}
                    className="text-[11px] uppercase tracking-wide text-grayscale-1100 font-grotesk">{s.title}</div>
                  {s.action && (
                    <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={s.action.onClick}
                      className="text-[11px] text-primary-700 underline">{s.action.label}</button>
                  )}
                </div>
                {s.entries.map(e => {
                  idx += 1;
                  const i = idx;
                  const on = i === activeIdx;
                  return (
                    <div key={e.key} id={optId(i)} role="option" aria-selected={on}
                      onMouseMove={() => { if (!on) { setActiveKey(e.key); setAlt(-1); } }}
                      onMouseDown={ev => ev.preventDefault()}
                      onClick={ev => go(e, -1, ev.metaKey || ev.ctrlKey)}
                      className={`mx-2 px-3 py-2 rounded-lg flex items-center gap-3 cursor-pointer
                        ${on ? 'bg-primary-100' : ''}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-sm truncate ${on ? 'text-primary-700 font-medium' : 'text-grayscale-1300'}`}>
                            {e.label}
                          </span>
                          {e.badge && <Badge tone={badgeTone(e.badge)}>{e.badge}</Badge>}
                        </div>
                        {e.sub && <div className="text-xs text-grayscale-1100 truncate">{e.sub}</div>}
                      </div>
                      {e.alt && (
                        <div className="flex flex-wrap justify-end gap-1 shrink-0 max-w-[45%]">
                          {e.alt.map((a, ai) => (
                            <a key={`${ai}-${a.label}`} href={adminHref(a.link)} tabIndex={-1}
                              onMouseDown={ev => ev.preventDefault()}
                              onClick={ev => {
                                ev.stopPropagation();
                                // Let the browser open new-tab clicks on the real URL.
                                if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button !== 0) return;
                                ev.preventDefault();
                                go(e, ai, false);
                              }}
                              className={`text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap
                                ${on && alt === ai
                                  ? 'border-primary-500 ring-1 ring-primary-500 text-primary-700 bg-white'
                                  : 'border-grayscale-200 text-grayscale-1200 bg-white hover:bg-grayscale-200'}`}>
                              {a.label}
                            </a>
                          ))}
                        </div>
                      )}
                      {on && <span aria-hidden className="text-[11px] text-grayscale-1100 shrink-0">↵</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {!flat.length && qq && !srv.loading && (
            <div className="px-4 py-8 text-center text-sm text-grayscale-1100">
              Nothing matches “{qq}”.
              {qq.length < 2 && <div className="text-xs mt-1">Type at least 2 characters to search records.</div>}
            </div>
          )}
          {!flat.length && qq && srv.loading && (
            <div className="px-4 py-8 text-center text-sm text-grayscale-1100">Searching…</div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 border-t border-grayscale-200 text-[11px] text-grayscale-1100">
          <span><Kbd>↑</Kbd> <Kbd>↓</Kbd> move</span>
          <span><Kbd>Enter</Kbd> open</span>
          <span><Kbd>{MOD}</Kbd> <Kbd>Enter</Kbd> new tab</span>
          {anyAlt && <span><Kbd>Tab</Kbd> other links</span>}
          <span className="ml-auto">
            {kind ? `Read as ${kind}` : ''}
            {kind && typeof srv.data?.took_ms === 'number' ? ` · ${srv.data.took_ms} ms` : ''}
          </span>
        </div>
        <div className="sr-only" aria-live="polite">{status}</div>
      </div>
    </div>
  );
};

export default CommandPalette;
